import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";

interface DashboardDateRangePickerProps {
  date?: DateRange;
  setDate: (date: DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
}

/** Separate start/end controls used only on Access Point / Xpand Point dashboards. */
export function DashboardDateRangePicker({
  date,
  setDate,
  placeholder = "Select date",
  className,
}: DashboardDateRangePickerProps) {
  const [startOpen, setStartOpen] = React.useState(false);
  const [endOpen, setEndOpen] = React.useState(false);

  const handleStartSelect = (selected: Date | undefined) => {
    if (!selected) {
      setDate(undefined);
      return;
    }

    const nextTo =
      date?.to && selected.getTime() > date.to.getTime() ? undefined : date?.to;

    setDate({ from: selected, to: nextTo });
    setStartOpen(false);

    if (!nextTo) {
      setEndOpen(true);
    }
  };

  const handleEndSelect = (selected: Date | undefined) => {
    if (!selected) {
      setDate(date?.from ? { from: date.from, to: undefined } : undefined);
      return;
    }

    if (date?.from && selected.getTime() < date.from.getTime()) {
      setDate({ from: selected, to: date.from });
    } else {
      setDate({ from: date?.from, to: selected });
    }
    setEndOpen(false);
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3",
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <Popover open={startOpen} onOpenChange={setStartOpen}>
          <PopoverTrigger asChild>
            <Button
              id="dashboard-start-date"
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal border-blue-200 bg-blue-50/60 hover:bg-blue-50",
                !date?.from && "text-muted-foreground",
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-blue-600" />
              {date?.from ? (
                <span className="text-blue-900">
                  {format(date.from, "MMM dd, yyyy")}
                </span>
              ) : (
                <span>{placeholder}</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="border-b bg-blue-50 px-3 py-2 text-sm font-medium text-blue-900">
              Select start date
            </div>
            <Calendar
              mode="single"
              selected={date?.from}
              onSelect={handleStartSelect}
              initialFocus
              defaultMonth={date?.from}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="hidden text-muted-foreground sm:block">→</div>

      <div className="min-w-0 flex-1">
        <Popover open={endOpen} onOpenChange={setEndOpen}>
          <PopoverTrigger asChild>
            <Button
              id="dashboard-end-date"
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50",
                !date?.to && "text-muted-foreground",
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-emerald-600" />
              {date?.to ? (
                <span className="text-emerald-900">
                  {format(date.to, "MMM dd, yyyy")}
                </span>
              ) : (
                <span>{placeholder}</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <div className="border-b bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-900">
              Select end date
            </div>
            <Calendar
              mode="single"
              selected={date?.to}
              onSelect={handleEndSelect}
              initialFocus
              defaultMonth={date?.to || date?.from}
              disabled={date?.from ? { before: date.from } : undefined}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
